// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { Modal, InlineNotification } from '@carbon/react';
import { BrowserAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../../config/authConfig';
import * as authService from '../../services/authService';
import { useAuth } from '../../hooks/useAuth';
import { useSessionGuardStore, type LockReason } from '../../stores/sessionGuardStore';
import { stashReturnPath } from '../../utils/returnPath';
import './LockOverlay.css';

interface LockOverlayProps {
  reason: LockReason;
}

type Panel = 'unlock' | 'different-user';

/**
 * A dedicated, static redirect target for the popup requests below — see
 * `public/blank.html`. Must be registered as a valid redirect URI on the
 * CIAM app registration alongside the default one (out of this repo's
 * control; flagged in ADR-0023).
 */
const POPUP_REDIRECT_URI = `${window.location.origin}/blank.html`;

/**
 * True when `loginPopup` failed because the browser blocked or couldn't open
 * the popup window at all (msal-browser's `popup_window_error` /
 * `empty_window_error`), as opposed to a popup that opened fine but was
 * cancelled or failed inside — those get the generic fallback instead, since
 * unlike a blocked popup they aren't fixed by a browser-settings change.
 */
function isPopupBlockedError(error: unknown): boolean {
  return (
    error instanceof BrowserAuthError &&
    (error.errorCode === 'popup_window_error' || error.errorCode === 'empty_window_error')
  );
}

const POPUP_BLOCKED_MESSAGE =
  'Your browser blocked the sign-in window. Allow pop-ups for this site, then try again.';

const DEACTIVATED_MESSAGE = 'Your account has been deactivated. Contact your administrator.';

/**
 * Not a lost session: IAM says the account is deactivated, so unlocking
 * can't help. Only sign-out is offered.
 */
function AccountDisabledOverlay() {
  // A second logoutRedirect while the first is in flight throws MSAL's
  // interaction_in_progress.
  const [signingOut, setSigningOut] = useState(false);
  return (
    <Modal
      open
      size="sm"
      className="lock-overlay-modal"
      modalHeading="Account deactivated"
      primaryButtonText={signingOut ? 'Signing out…' : 'Sign out'}
      primaryButtonDisabled={signingOut}
      onRequestSubmit={() => {
        if (signingOut) return;
        setSigningOut(true);
        authService.logout().catch(error => {
          console.error('[auth] Sign-out failed —', error);
          setSigningOut(false);
        });
      }}
      onRequestClose={() => {}}
      preventCloseOnClickOutside
    >
      <p className="lock-overlay-body">{DEACTIVATED_MESSAGE}</p>
    </Modal>
  );
}

function LockOverlay({ reason }: LockOverlayProps) {
  if (reason === 'account_disabled') return <AccountDisabledOverlay />;
  return <SessionLockOverlay reason={reason} />;
}

/**
 * The screen is locked but never unmounted: a Carbon `Modal` (transactional,
 * not `passiveModal`) sits on top of whatever the user was looking at
 * (ADO-25799), matching the rest of the app's modal styling exactly instead
 * of a hand-rolled approximation of it. Non-dismissable except by its own
 * buttons — see the `IdleWarningModal` doc comment for why `onRequestClose`
 * is a no-op and the close icon is hidden via CSS. Triggered by idle timeout
 * or a 401 from any API call (ADO-25798) — same overlay either way.
 *
 * Neither panel renders a credential field (ADO-30927 removed the decorative
 * disabled email/password inputs from the original mockup, which misrepresented
 * how auth actually works here): "Unlock" and "Sign in as a different user"
 * both authenticate entirely via `msalInstance.loginPopup` — CIAM validates
 * the credential inside a popup, never a locally-handled password. Do not add
 * a local credential form back — that would contradict this app's MSAL-only
 * auth policy (see Login.tsx's own header comment and CLAUDE.md's
 * BFF/JWT-forwarding architecture). "Sign in as a different user" passes
 * `prompt: 'select_account'` so CIAM always shows the account picker instead
 * of silently re-authenticating whichever account is still cached.
 */
function SessionLockOverlay({ reason }: LockOverlayProps) {
  const { user } = useAuth();
  const [panel, setPanel] = useState<Panel>('unlock');
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const name = user ? `${user.first_name} ${user.last_name}`.trim() : 'Signed in user';
  const initials = (user?.first_name?.[0] ?? '') + (user?.last_name?.[0] ?? '');

  const goToFullSignIn = () => {
    stashReturnPath(window.location.pathname + window.location.search);
    void authService.logout();
  };

  const handleUnlock = async () => {
    if (isAuthenticating) return;
    setError(null);
    setIsAuthenticating(true);
    try {
      const result = await msalInstance.loginPopup({
        ...loginRequest,
        redirectUri: POPUP_REDIRECT_URI,
      });
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
        // A 403 ACCOUNT_DISABLED may have landed while the popup was open;
        // signing in again doesn't reactivate the account.
        if (useSessionGuardStore.getState().lockReason !== 'account_disabled') {
          useSessionGuardStore.getState().unlock();
        }
      } else {
        goToFullSignIn();
      }
    } catch (err) {
      if (isPopupBlockedError(err)) {
        setError(POPUP_BLOCKED_MESSAGE);
      } else {
        goToFullSignIn();
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleSignInAsDifferentUser = async () => {
    if (isAuthenticating) return;
    setError(null);
    setIsAuthenticating(true);
    try {
      const result = await msalInstance.loginPopup({
        ...loginRequest,
        prompt: 'select_account',
        redirectUri: POPUP_REDIRECT_URI,
      });
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
        useSessionGuardStore.getState().setSignInAt(Date.now());
        useSessionGuardStore.getState().unlock();
        window.location.assign('/');
      } else {
        setError('Sign-in was cancelled. Please try again.');
      }
    } catch (err) {
      if (isPopupBlockedError(err)) {
        setError(POPUP_BLOCKED_MESSAGE);
      } else {
        setError('Sign-in failed. Please try again.');
      }
    } finally {
      setIsAuthenticating(false);
    }
  };

  const isUnlockPanel = panel === 'unlock';

  return (
    <Modal
      open
      size="sm"
      className="lock-overlay-modal"
      modalHeading={isUnlockPanel ? 'Session locked' : 'Sign in'}
      primaryButtonText={isAuthenticating ? 'Signing in…' : isUnlockPanel ? 'Unlock' : 'Sign In'}
      primaryButtonDisabled={isAuthenticating}
      secondaryButtonText={isUnlockPanel ? 'Sign in as a different user' : 'Back'}
      onRequestSubmit={isUnlockPanel ? handleUnlock : handleSignInAsDifferentUser}
      onSecondarySubmit={
        isUnlockPanel
          ? () => {
              setError(null);
              setPanel('different-user');
            }
          : () => {
              setError(null);
              setPanel('unlock');
            }
      }
      onRequestClose={() => {}}
      preventCloseOnClickOutside
    >
      {isUnlockPanel ? (
        <>
          <div className="lock-overlay-user">
            <span className="lock-overlay-avatar" aria-hidden="true">
              {initials.toUpperCase() || '—'}
            </span>
            <div>
              <div className="lock-overlay-name">{name}</div>
              {user?.email && <div className="lock-overlay-email">{user.email}</div>}
            </div>
          </div>

          <p className="lock-overlay-body">
            {reason === 'unauthorized'
              ? 'Sign in again to continue.'
              : 'You were signed out for inactivity.'}
          </p>
        </>
      ) : (
        <p className="lock-overlay-body">
          Choose the Microsoft account you&apos;d like to sign in with.
        </p>
      )}

      {error && (
        <InlineNotification kind="error" lowContrast title={error} hideCloseButton role="alert" />
      )}
    </Modal>
  );
}

export default LockOverlay;
